import bellRingingIcon from "@tabler/icons/outline/bell-ringing.svg";
import bellIcon from "@tabler/icons/outline/bell.svg";
import React from "react";
import { defineMessages, useIntl } from "react-intl";

import { useSubscribeToGroup, useUnsubscribeToGroup } from "@/api/groups";
import { IconButton } from "@/components/ui";
import toast from "@/toast";

import type { Group } from "@/schemas";

const messages = defineMessages({
  subscribe: {
    id: "group.subscribe",
    defaultMessage: "Subscribe to alerts from @{name}",
  },
  unsubscribe: {
    id: "group.unsubscribe",
    defaultMessage: "Unsubscribe to alerts from @{name}",
  },
  subscribeSuccess: {
    id: "group.subscribe.success",
    defaultMessage: "You have subscribed to this group.",
  },
  unsubscribeSuccess: {
    id: "group.unsubscribe.success",
    defaultMessage: "You have unsubscribed from this group.",
  },
  subscribeFailure: {
    id: "group.subscribe.failure",
    defaultMessage: "An error occurred trying to subscribe to this group.",
  },
  unsubscribeFailure: {
    id: "group.unsubscribe.failure",
    defaultMessage: "An error occurred trying to unsubscribe to this group.",
  },
});

interface GroupSubscriptionButtonProps {
  group: Group;
}

const GroupSubscriptionButton = ({ group }: GroupSubscriptionButtonProps) => {
  const intl = useIntl();

  const isMember = group.relationship?.member;
  const isSubscribed = group.relationship?.notifying;

  const subscribeToGroup = useSubscribeToGroup();
  const unsubscribeToGroup = useUnsubscribeToGroup();

  const title = isSubscribed
    ? intl.formatMessage(messages.unsubscribe, { name: group.display_name })
    : intl.formatMessage(messages.subscribe, { name: group.display_name });

  const onSubscribe = () =>
    subscribeToGroup.mutate(
      { id: group.relationship!.id },
      {
        onSuccess() {
          toast.success(intl.formatMessage(messages.subscribeSuccess));
        },
      },
    );

  const onUnsubscribe = () =>
    unsubscribeToGroup.mutate(
      { id: group.relationship!.id },
      {
        onSuccess() {
          toast.success(intl.formatMessage(messages.unsubscribeSuccess));
        },
      },
    );

  const handleToggle = () => (isSubscribed ? onUnsubscribe() : onSubscribe());

  if (isMember) {
    return (
      <IconButton
        src={isSubscribed ? bellRingingIcon : bellIcon}
        onClick={handleToggle}
        title={title}
        variant="outline"
        className="self-stretch px-2.5"
        iconClassName="h-5 w-5"
      />
    );
  }

  return null;
};

export default GroupSubscriptionButton;
